import { useCallback, useEffect, useRef, useState } from "react";

export interface AudioMeter {
  level: number;
  levelRef: React.RefObject<number>;
  start: () => Promise<MediaStream>;
  stop: () => void;
}

export function useAudioMeter(onVoice?: (at: number, level: number) => void): AudioMeter {
  const [level, setLevel] = useState(0);
  const levelRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastPaintRef = useRef(0);
  const callbackRef = useRef(onVoice);
  callbackRef.current = onVoice;

  const stop = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    levelRef.current = 0;
    setLevel(0);
  }, []);

  const start = useCallback(async () => {
    stop();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
        channelCount: 1,
      },
    });
    const context = new AudioContext({ latencyHint: "interactive" });
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.76;
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    streamRef.current = stream;
    contextRef.current = context;

    const read = (at: number) => {
      analyser.getFloatTimeDomainData(samples);
      let energy = 0;
      for (const sample of samples) energy += sample * sample;
      const rms = Math.sqrt(energy / samples.length);
      const normalized = Math.min(1, Math.max(0, (rms - 0.008) * 8.5));
      levelRef.current = normalized;
      if (normalized > 0.06) callbackRef.current?.(performance.now(), normalized);
      if (at - lastPaintRef.current > 40) {
        lastPaintRef.current = at;
        setLevel(normalized);
      }
      frameRef.current = requestAnimationFrame(read);
    };
    frameRef.current = requestAnimationFrame(read);
    return stream;
  }, [stop]);

  useEffect(() => stop, [stop]);
  return { level, levelRef, start, stop };
}
